/**
 * Detalle / edición de instrucción del catálogo n8n (nexo_catalogs.n8n_instructions).
 *
 * @module presentation/views/InstructionDetailView
 */

import { InstructionApiClient } from '../../infrastructure/api/InstructionApiClient.js';
import { renderEditorToolbar, bindEditorToolbar } from '../shared/DocumentViewer.js';
import { confirmar } from '../shared/nxDialogo.js';

// Deuda 4: este escape (no cambiaba la comilla simple) es ahora el compartido.
import { esc } from '../shared/texto.js';
import { insigniaHtml, COLOR_ESTADO } from '../shared/insignia.js';
import { regresoHtml } from '../shared/regreso.js';
import { formatearFecha } from '../shared/fechaColombiana.js';

function badge(status) {
  return insigniaHtml(esc(status), status === 'active' ? COLOR_ESTADO.ok : COLOR_ESTADO.apagado);
}

/** Primer valor definido (API camelCase / edge PascalCase). */
function pick(obj, ...keys) {
  if (!obj) return '';
  for (const k of keys) {
    const v = obj[k];
    if (v != null && v !== '') return v;
  }
  return '';
}

function normalizeSteps(raw) {
  const arr = Array.isArray(raw) ? raw : [];
  return arr.map((s) => ({
    paramName: pick(s, 'paramName', 'ParamName'),
    stepOrder: s.stepOrder ?? s.StepOrder ?? 0,
    sqlQuery: pick(s, 'sqlQuery', 'SqlQuery'),
    description: pick(s, 'description', 'Description')
  }));
}

/**
 * Aplana ApiResponse { success, data: InstructionDto } y alias de propiedades.
 */
function normalizeInstruction(payload) {
  const root = payload?.data !== undefined ? payload.data : payload;
  const inst = root?.data !== undefined ? root.data : root;
  if (!inst || typeof inst !== 'object') return null;

  const steps = normalizeSteps(inst.steps ?? inst.Steps);
  return {
    id: pick(inst, 'id', 'Id'),
    code: pick(inst, 'code', 'Code'),
    title: String(pick(inst, 'title', 'Title') || '').trim(),
    instructionVersion: String(pick(inst, 'instructionVersion', 'instruction_version', 'InstructionVersion') || '').trim(),
    area: String(pick(inst, 'area', 'Area') || '').trim(),
    layerScope: String(pick(inst, 'layerScope', 'layer_scope', 'LayerScope') || '').trim(),
    status: (pick(inst, 'status', 'Status') || 'active').toLowerCase() === 'archived' ? 'archived' : 'active',
    variablesSchemaJson: pick(inst, 'variablesSchemaJson', 'VariablesSchemaJson'),
    stepCount: inst.stepCount ?? inst.StepCount ?? steps.length,
    createdAt: inst.createdAt ?? inst.CreatedAt,
    updatedAt: inst.updatedAt ?? inst.UpdatedAt,
    steps
  };
}

function fmtTs(iso) {
  // Sin fecha o invalida, formatearFecha ya da '—' (como el try de antes).
  return formatearFecha(iso, { dateStyle: 'short', timeStyle: 'short' }, { parte: 'todo' });
}

export async function renderInstructionDetail(container) {
  const parts = window.location.hash.split('/');
  const id = parts[parts.length - 1];
  if (!id) {
    container.innerHTML = '<p class="empty-state">ID inválido</p>';
    return;
  }

  container.innerHTML = '<p class="empty-state">Cargando...</p>';

  const result = await InstructionApiClient.getById(id);
  if (!result.ok) {
    container.innerHTML = `
      <section class="artifact-view">
        ${regresoHtml({ volverA: "#/instructions", texto: "Instrucciones" })}
        <p class="empty-state">Error: ${esc(result.error)}</p>
      </section>`;
    return;
  }

  const inst = normalizeInstruction(result.data);
  if (!inst) {
    container.innerHTML = `
      <section class="artifact-view">
        ${regresoHtml({ volverA: "#/instructions", texto: "Instrucciones" })}
        <p class="empty-state">Respuesta inválida del servidor</p>
      </section>`;
    return;
  }

  renderForm(container, inst);
}

function renderForm(container, inst) {
  const steps = inst.steps || [];
  const verLabel = inst.instructionVersion || '—';
  const desc0 = steps[0]?.description || '';

  const stepsHtml = steps.length
    ? `<div class="nx-inst-section nx-inst-detail-steps">
        <h3 class="nx-inst-section-title">Pasos SQL <span class="nx-inst-section-count">${steps.length}</span></h3>
        <div class="nx-inst-sql-stack">
          ${steps.map((s) => `
            <div class="nx-inst-sql-card">
              <div class="nx-inst-sql-head">
                <code>${esc(s.paramName || 'paso')}</code>
                <span class="nx-inst-sql-ord">orden ${esc(String(s.stepOrder))}</span>
              </div>
              <pre class="nx-inst-sql-pre">${esc(s.sqlQuery || '(sin sql_query)')}</pre>
            </div>`).join('')}
        </div>
      </div>`
    : '';

  const varsVal = inst.variablesSchemaJson
    ? String(inst.variablesSchemaJson)
    : '';

  container.innerHTML = `
    <section class="artifact-view nx-inst-detail">
      <div class="artifact-header nx-inst-hero">
        <div class="nx-inst-hero-main">
          ${regresoHtml({ volverA: "#/instructions", texto: "Instrucciones" })}
          <h1 class="nx-inst-detail-title">${esc(inst.title || inst.code || 'Instrucción')}</h1>
          <p class="subtitle nx-inst-detail-sub">
            <code class="instruction-code">${esc(inst.code)}</code>
            <span class="nx-inst-pill-sep">·</span> versión <strong>${esc(verLabel)}</strong>
            <span class="nx-inst-pill-sep">·</span> ${inst.stepCount || steps.length} paso(s)
            <span class="nx-inst-pill-sep">·</span> actualizado ${esc(fmtTs(inst.updatedAt))}
          </p>
          <div class="nx-inst-meta-strip" role="group" aria-label="Metadatos de catálogo">
            <div class="nx-inst-meta-item"><span class="nx-inst-meta-k">instruction_id</span><code class="nx-inst-meta-v">${esc(inst.code)}</code></div>
            <div class="nx-inst-meta-item nx-inst-meta-item--grow"><span class="nx-inst-meta-k">UUID fila</span><code class="nx-inst-meta-v nx-inst-meta-uuid">${esc(inst.id)}</code></div>
            <div class="nx-inst-meta-item"><span class="nx-inst-meta-k">creado</span><span class="nx-inst-meta-v">${esc(fmtTs(inst.createdAt))}</span></div>
          </div>
        </div>
        <div class="nx-inst-hero-badge">${badge(inst.status)}</div>
      </div>

      <div class="nx-inst-detail-card">
        <form id="nx-inst-detail-form" class="nx-inst-detail-form">
          <input type="hidden" id="nx-inst-detail-id" value="${esc(inst.id)}">

          <div class="nx-inst-section">
            <h3 class="nx-inst-section-title">Datos generales</h3>
            <div class="nx-inst-grid nx-inst-grid-2">
              <div class="nx-inst-field">
                <label class="detail-label" for="nx-inst-detail-title">Título</label>
                <input type="text" id="nx-inst-detail-title" class="form-input nx-inst-input" required autocomplete="off">
              </div>
              <div class="nx-inst-field">
                <label class="detail-label" for="nx-inst-detail-ver">Versión <span class="nx-inst-lbl-hint">(instruction_version)</span></label>
                <input type="text" id="nx-inst-detail-ver" class="form-input nx-inst-input" placeholder="ej. 2.1.0" autocomplete="off">
              </div>
              <div class="nx-inst-field">
                <label class="detail-label" for="nx-inst-detail-area">Área <span class="nx-inst-lbl-hint">(target_product)</span></label>
                <input type="text" id="nx-inst-detail-area" class="form-input nx-inst-input" placeholder="Nexo Center Panel" autocomplete="off">
              </div>
              <div class="nx-inst-field">
                <label class="detail-label" for="nx-inst-detail-fw">Framework</label>
                <input type="text" id="nx-inst-detail-fw" class="form-input nx-inst-input" placeholder="n8n-workflow" autocomplete="off">
              </div>
            </div>
            <div class="nx-inst-field nx-inst-field--status">
              <label class="detail-label" for="nx-inst-detail-status">Estado</label>
              <select id="nx-inst-detail-status" class="form-input form-input-sm nx-inst-select">
                <option value="active">active</option>
                <option value="archived">archived</option>
              </select>
            </div>
          </div>

          <div class="nx-inst-section">
            <h3 class="nx-inst-section-title">Descripción <span class="nx-inst-lbl-hint">(primer paso SQL)</span></h3>
            ${renderEditorToolbar('nx-inst-detail-desc', 'nx-inst-desc-preview')}
            <textarea id="nx-inst-detail-desc" class="form-input form-textarea nx-inst-textarea-desc" rows="6" placeholder="Texto asociado al paso con menor orden…" spellcheck="true"></textarea>
            <div id="nx-inst-desc-preview" hidden></div>
          </div>

          <div class="nx-inst-section">
            <h3 class="nx-inst-section-title">variables_schema <span class="nx-inst-lbl-hint">JSON</span></h3>
            <textarea id="nx-inst-detail-vars" class="form-input form-textarea nx-inst-json-editor" rows="16" placeholder="{ }" spellcheck="false"></textarea>
            <p class="nx-inst-hint">JSON válido. Se aplica a todas las filas con el mismo <code>instruction_id</code> al guardar.</p>
          </div>

          ${stepsHtml}

          <div class="nx-inst-actions">
            <button type="submit" class="btn-primary nx-inst-btn-save">Guardar cambios</button>
            <div class="nx-inst-actions-secondary">
              <button type="button" class="btn-ghost nx-inst-del" id="nx-inst-detail-del">Eliminar instrucción completa</button>
              <span id="nx-inst-detail-err" class="auth-error" hidden></span>
            </div>
          </div>
        </form>
      </div>
    </section>`;

  bindEditorToolbar(container);

  document.getElementById('nx-inst-detail-title').value = inst.title || '';
  document.getElementById('nx-inst-detail-ver').value = inst.instructionVersion || '';
  document.getElementById('nx-inst-detail-area').value = inst.area || '';
  document.getElementById('nx-inst-detail-fw').value = inst.layerScope || '';
  document.getElementById('nx-inst-detail-status').value = inst.status === 'archived' ? 'archived' : 'active';
  document.getElementById('nx-inst-detail-desc').value = desc0;
  document.getElementById('nx-inst-detail-vars').value = varsVal;

  document.getElementById('nx-inst-detail-form').onsubmit = async (e) => {
    e.preventDefault();
    const $err = document.getElementById('nx-inst-detail-err');
    $err.hidden = true;
    const varsRaw = document.getElementById('nx-inst-detail-vars').value;
    const varsTrim = varsRaw.trim();
    if (varsTrim) {
      try {
        JSON.parse(varsTrim);
      } catch {
        $err.textContent = 'variables_schema no es JSON válido';
        $err.hidden = false;
        return;
      }
    }
    const payload = {
      title: document.getElementById('nx-inst-detail-title').value.trim(),
      instructionVersion: document.getElementById('nx-inst-detail-ver').value.trim() || null,
      area: document.getElementById('nx-inst-detail-area').value.trim(),
      layerScope: document.getElementById('nx-inst-detail-fw').value.trim(),
      status: document.getElementById('nx-inst-detail-status').value,
      body: document.getElementById('nx-inst-detail-desc').value.trim(),
      variablesSchemaJson: varsTrim
    };
    const rid = document.getElementById('nx-inst-detail-id').value;
    const res = await InstructionApiClient.update(rid, payload);
    if (res.ok) {
      await renderInstructionDetail(container);
    } else {
      $err.textContent = res.error || 'Error al guardar';
      $err.hidden = false;
    }
  };

  document.getElementById('nx-inst-detail-del').onclick = async () => {
    if (!await confirmar({ titulo: `Eliminar por completo la instrucción ${inst.code}`, texto: 'Se borrarán todos los pasos en catálogo n8n.', aceptar: 'Eliminar instrucción', peligro: true })) return;
    const rid = document.getElementById('nx-inst-detail-id').value;
    const res = await InstructionApiClient.remove(rid);
    if (res.ok) {
      window.location.hash = '#/instructions';
    } else {
      const $err = document.getElementById('nx-inst-detail-err');
      $err.textContent = res.error || 'Error al eliminar';
      $err.hidden = false;
    }
  };
}
